import * as THREE from 'three';
import SceneManager from '../SceneManager';
import { global } from '../GlobalState';
/**
 * Binds the local client runtime (camera, controls, browser/session metadata)
 * to a hidden-by-camera orb that can still participate in world systems such as reflections.
 * This class is designed as a future multiplayer handoff point for backend sync.
 */
export class ClientStateOrbContext {
    constructor(args) {
        this.orb = null;
        this.profile = {
            clientId: args.clientId,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
            locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
            timezone: typeof Intl !== 'undefined'
                ? Intl.DateTimeFormat().resolvedOptions().timeZone
                : undefined,
        };
        this.camera = args.camera;
        this.controls = args.controls;
        this.controlsManager = args.controlsManager;
        this.sessionState = {
            inputLocked: false,
            hyperspeed: false,
            movementSpeed: 16,
            movementDrag: 8,
            cameraPosition: this.camera.position.clone(),
            cameraRotation: this.camera.rotation.clone(),
        };
    }
    bindOrb(orb) {
        this.orb = orb;
        this.detachFromSharedMaterials();
        this.applyOwnerCameraVisibilityRule();
        this.syncFromRuntime();
    }
    detachFromSharedMaterials() {
        var _a;
        if (!((_a = this.orb) === null || _a === void 0 ? void 0 : _a.group))
            return;
        this.orb.group.traverse((node) => {
            const mesh = node;
            if (!mesh.isMesh || !mesh.material)
                return;
            if (Array.isArray(mesh.material)) {
                mesh.material = mesh.material.map((mat) => {
                    if (mat.__ownerCameraUniqueMaterial)
                        return mat;
                    const cloned = mat.clone();
                    cloned.__ownerCameraUniqueMaterial = true;
                    return cloned;
                });
                return;
            }
            if (mesh.material.__ownerCameraUniqueMaterial)
                return;
            const cloned = mesh.material.clone();
            cloned.__ownerCameraUniqueMaterial = true;
            mesh.material = cloned;
        });
    }
    applyOwnerCameraVisibilityRule() {
        var _a;
        if (!((_a = this.orb) === null || _a === void 0 ? void 0 : _a.group))
            return;
        this.orb.group.traverse((node) => {
            const mesh = node;
            if (!mesh.isMesh || !mesh.material)
                return;
            const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            mesh.onBeforeRender = (_renderer, _scene, camera) => {
                var _a, _b;
                const hideForOwnerCamera = camera === this.camera;
                for (const material of materials) {
                    const mat = material;
                    if (mat.__ownerCameraColorWrite === undefined) {
                        mat.__ownerCameraColorWrite = mat.colorWrite;
                    }
                    if (mat.__ownerCameraDepthWrite === undefined) {
                        mat.__ownerCameraDepthWrite = mat.depthWrite;
                    }
                    mat.colorWrite = hideForOwnerCamera ? false : ((_a = mat.__ownerCameraColorWrite) !== null && _a !== void 0 ? _a : true);
                    mat.depthWrite = hideForOwnerCamera ? false : ((_b = mat.__ownerCameraDepthWrite) !== null && _b !== void 0 ? _b : true);
                }
            };
        });
    }
    syncFromRuntime(stateOverrides) {
        this.sessionState = Object.assign(Object.assign(Object.assign({}, this.sessionState), { inputLocked: this.controls.isLocked, cameraPosition: this.camera.position.clone(), cameraRotation: this.camera.rotation.clone() }), stateOverrides);
        if (this.orb) {
            this.applyOwnerCameraVisibilityRule();
            this.orb.setPosition(this.sessionState.cameraPosition);
            this.orb.jsonData = this.buildLivePayload();
        }
    }
    buildLivePayload() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        const lookDirection = this.camera.getWorldDirection(new THREE.Vector3());
        const sceneName = (_a = SceneManager.getInstance().getSceneNameByInstance(this.camera.parent)) !== null && _a !== void 0 ? _a : null;
        const authState = (_d = (_c = (_b = global.auth) === null || _b === void 0 ? void 0 : _b.getState) === null || _c === void 0 ? void 0 : _c.call(_b)) !== null && _d !== void 0 ? _d : { status: 'anonymous' };
        return {
            clientId: this.profile.clientId,
            label: ((_e = this.orb) === null || _e === void 0 ? void 0 : _e.labelText) || ((_f = this.orb) === null || _f === void 0 ? void 0 : _f.orbType) || 'client',
            clientProfile: Object.assign({}, this.profile),
            clientSession: {
                inputLocked: this.sessionState.inputLocked,
                hyperspeed: this.sessionState.hyperspeed,
                movementSpeed: this.sessionState.movementSpeed,
                movementDrag: this.sessionState.movementDrag,
                cameraPosition: this.sessionState.cameraPosition.toArray(),
                cameraRotation: [
                    this.sessionState.cameraRotation.x,
                    this.sessionState.cameraRotation.y,
                    this.sessionState.cameraRotation.z,
                ],
                lookDirection: lookDirection.toArray(),
                worldUp: this.camera.up.toArray(),
                activeScene: sceneName,
                currentWorld: global.hudCurrentWorld,
                activeAbilityMode: global.clickMode,
                orbitingTargetId: (_j = (_h = (_g = global.orbitingTarget) === null || _g === void 0 ? void 0 : _g.group) === null || _h === void 0 ? void 0 : _h.uuid) !== null && _j !== void 0 ? _j : null,
                semanticContext: [...((_k = global.hudSemanticContext) !== null && _k !== void 0 ? _k : [])],
                rendererPixelMode: global.rendererPixelMode,
                online: typeof navigator !== 'undefined' ? navigator.onLine : true,
                pointerLockElement: typeof document !== 'undefined' ? ((_m = (_l = document.pointerLockElement) === null || _l === void 0 ? void 0 : _l.tagName) !== null && _m !== void 0 ? _m : null) : null,
                timestampIso: new Date().toISOString(),
            },
            controlState: {
                schemeId: (_q = (_p = (_o = this.controlsManager).getCurrentSchemeId) === null || _p === void 0 ? void 0 : _p.call(_o)) !== null && _q !== void 0 ? _q : null,
                inputLocked: this.controls.isLocked,
            },
            authState,
            worldState: {
                activeScene: sceneName,
                loadedWorlds: global.sceneManager.listScenes().length,
            },
            // Future intent: expose a structured, inspectable model of browser/client runtime
            // so players can safely introspect and eventually live-edit their own client state/code.
            runtimeIntrospectionPlanned: true,
        };
    }
    getOrb() {
        return this.orb;
    }
    snapshotForNetworking() {
        var _a, _b, _c;
        return {
            profile: Object.assign({}, this.profile),
            session: {
                inputLocked: this.sessionState.inputLocked,
                hyperspeed: this.sessionState.hyperspeed,
                movementSpeed: this.sessionState.movementSpeed,
                movementDrag: this.sessionState.movementDrag,
                cameraPosition: this.sessionState.cameraPosition.clone(),
                cameraRotation: this.sessionState.cameraRotation.clone(),
            },
            controlSchemeId: (_c = (_b = (_a = this.controlsManager).getCurrentSchemeId) === null || _b === void 0 ? void 0 : _b.call(_a)) !== null && _c !== void 0 ? _c : null,
        };
    }
}
